// ============================================================
// WHAT ONE KNOWS OF ONESELF - the `self` part of a Situation.
//
// A condition may ask about oneself (`own_state`, `own_stance`): one's
// conditions, wounds, critical damage, fatigue, frenzy and what is up (Always on, Solo modes).
// Two readers ask it, and must hear the same answer:
//   - the attack's situational pass (`hooks/useSituationalEffects`);
//   - the effective sheet (`roster/stancedSheet`), for a SHEET effect that
//     waits on oneself ("Toughness +10 while wounded").
//
// Pure. Zero React dependency.
// ============================================================
import { upNow } from './soloModes';
import type { Character } from './types';
import type { Situation } from './whenParts';

/** The live state a clause about oneself reads; every part optional, a missing one is not known. */
interface OwnState {
  conditions?: readonly string[];
  woundsCurrent?: number;
  criticalDamage?: readonly string[];
  frenzied?: boolean;
  activeStances?: readonly string[];
  /** In Squad Mode every Solo mode stands down (D5). */
  killTeam?: { on?: true };
}

const NONE: readonly string[] = [];

/** The names of the stances up: switched by hand, or triggered by a condition carried. */
const stancesUp = (sheet: Pick<Character, 'alwaysOn' | 'soloModes'>, state: OwnState): string[] =>
  upNow(sheet, state).map((u) => u.name);

/** Oneself as a clause asks about it. With no state, only that one carries no condition is known. */
export function selfFactsOf(
  sheet: Pick<Character, 'alwaysOn' | 'soloModes' | 'wounds' | 'fatigue'> | null | undefined,
  state: OwnState | null | undefined,
): NonNullable<Situation['self']> {
  if (!state) return { conditions: NONE };
  return {
    conditions: state.conditions ?? NONE,
    ...(sheet?.wounds && typeof state.woundsCurrent === 'number'
      ? { wounds: { current: state.woundsCurrent, max: sheet.wounds.max } }
      : {}),
    critical: !!state.criticalDamage?.length,
    fatigue: sheet?.fatigue ?? 0,
    frenzied: !!state.frenzied,
    stances: sheet ? stancesUp(sheet, state) : [],
  };
}
